import React, { useEffect, useState } from 'react';
import "./requestforvolunteeringservices.css";
import { Checkbox, DatePicker, Input, notification, Select } from 'antd';
import SelectPatientName from './components/SelectPatientName';
import SelectTypeOFServices from './components/SelectTypeOFServices';
import PrimaryButton from '../../../components/generic/buttons/PrimaryButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { currentConfiguration } from '../../../service/memory/recoil/configurationstore';
import ConfigurationConstants from '../../utils/configurationconstants';
import { executeIntent } from '../../utils/IntentExecutor';
import IntentType from '../../../intentexecutor/utils/IntentType';
import moment from 'moment';
import { useGetOrgIdentifier } from '../../generic/hooks/useGetOrgIdentifier';
import { convertDateToSODTimestamp, getDateString } from '../../../utils/dateutils';
import BackButton from '../../generic/elements/backbutton/BackButton';
import { disabledDate, getFullName } from '../../careprovider/visits/utils';
import { useReadMemory } from '../../generic/hooks/useReadMemory';
import MemoryType from '../../../service/memory/enum/MemoryType';
import { strings } from '../../utils/strings';
import { performPostRequest } from '../../../service/network/NetworkService';
import { getApiUrl } from '../../../service/network/UrlMapper';
import ErrorNotificationComponent from '../../../components/generic/error/ErrorNotificationComponent';
import CalenderAndDate from './components/CalenderAndDate';
import VolunteerFrequency from './components/VolunteerFrequency';
import MultiSelectCards from '../../../components/generic/cards/MultiSelectCards';
import { timeOfTheDay } from './scheduledetails/utils/scheduleData';
import apiService from '../../../service/apiservice/apiService';
import VolunteerTable from './components/VolunteerTable';
import VolunteerActivityTable from './components/VolunteerActivityTable';
import SecondaryButton from '../../../components/generic/buttons/SecondaryButton';
import CustomModal from '../../../components/generic/custommodal/CustomModal';
import { publishEvent } from '../../../components/generic/EventPublisher';
import WorkforceSuggestionWrapper from './components/WorkforceSuggestionWrapper';
import SmartIcon from '../../../components/generic/genericIcon/SmartIcon';
import SelectCarePlanStatus from './components/SelectCarePlanStatus';
import { statusCode } from '../../utils/statuscodes';

const { TextArea } = Input;

const carePlanErrorMessages = {
  [statusCode.REQUEST_ACCEPTANCE_LIMIT_REACHED]: strings.REQUEST_ACCEPTANCE_LIMIT_REACHED,
  [statusCode.OUTDATED_DATA]: strings.OUTDATED_DATA,
};

const personHasName = (person) =>
  Boolean(person?.name || person?.givenName || person?.familyName);

const displayWorkforce = (person, workforceIdentifier) => {
  if (!personHasName(person)) return null;
  return {
    givenName: person.givenName,
    familyName: person.familyName,
    ...(person.name ? { name: person.name } : {}),
    userIdentifier: person.userIdentifier || workforceIdentifier,
  };
};

const collectWorkforceById = ({
  opportunities,
  selectedUsers,
  previousOpportunities,
  frequency,
}) => {
  const map = new Map();
  const add = (id, person) => {
    if (!id || map.has(id)) return;
    const workforce = displayWorkforce(person, id);
    if (workforce) map.set(id, workforce);
  };

  (opportunities || []).forEach((item) =>
    add(item?.workforceIdentifier, item?.workforce),
  );
  (previousOpportunities || []).forEach((item) =>
    add(item?.workforceIdentifier, item?.workforce),
  );
  (selectedUsers || []).forEach((user) => add(user?.userIdentifier, user));

  const entries = Array.isArray(frequency) ? frequency : [];
  entries.forEach((entry) => {
    (entry?.slots || []).forEach((slot) => {
      add(slot?.assignedVolunteer?.userIdentifier, slot?.assignedVolunteer);
    });
  });

  return map;
};

const withWorkforceNames = (opportunityList, lookup) =>
  (opportunityList || []).map((opportunity) => {
    if (personHasName(opportunity?.workforce)) return opportunity;
    const workforce = lookup.get(opportunity?.workforceIdentifier);
    return workforce ? { ...opportunity, workforce } : opportunity;
  });

/** One opportunity per workforceIdentifier (last write wins). Strips nested workforce for API payloads. */
const uniqueOpportunitiesByWorkforce = (list = [], { stripWorkforce = false } = {}) => {
  const map = new Map();
  for (const item of list || []) {
    const key = item?.workforceIdentifier;
    if (!key) continue;
    const next = stripWorkforce
      ? (() => {
          const { workforce, ...rest } = item;
          return rest;
        })()
      : item;
    map.set(key, next);
  }
  return [...map.values()];
};

const RequestForVolunteeringService = () => {

  const userJson = useReadMemory(MemoryType.USER);
  const [patientName, setPatientName] = useState([]);
  const [typesOfServicesSelected, setTypesOfServicesSelected] = useState([]);
  const [typesOfSourceSelected, setTypesOfSourceSelected] = useState([]);
  const navigate = useNavigate();
  const organizationIdentifier = useGetOrgIdentifier();
  const query = new URLSearchParams(useLocation().search);
  const patientIdentifier = query.get('identifier');
  const carePlanId = query.get('carePlanId');
  const showCancel = query.get('showCancel');
  const assignVolunteer = query.get('assign');
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey = configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const [visitStartDate, setVisitStartDate] = useState("");
  const [carePlan, setCarePlan] = useState({
    requestDate: convertDateToSODTimestamp(getDateString()),
    frequencyPeriod: "monthly",
  });
  const [broadcastData, setBroadcastData] = useState({});
  const [submitBtnDisable, setSubmitBtnDisable] = useState(true);
  const [submitBtnDisableText, setSubmitBtnDisableText] = useState("Please select type of service required and visit start date to continue!");
  const [openVolunteerSuggestionModal, setOpenVolunteerSuggestionModal] = useState(false)
  const [shareWithSelectedVolunteers, setShareWithSelectedVolunteers] = useState(false)
  const [duration, setDuration] = useState({});
  const [carePlanLoading, setCarePlanLoading] = useState(false)
  const [opportunities, setOpportunities] = useState([])
  const [carePlanData, setCarePlanData] = useState({})
  const [isShared, setIsShared] = useState(false);
  const [isStartDateRequired, setIsStartDateRequired] = useState(true);
  const [isTeamRequired, setIsTeamRequired] = useState(true);
  const [defaultMonths, setDefaultMonths] = useState(7);
  const [acceptanceLimit, setAcceptanceLimit] = useState(0);
  const [requiredWithInRadius, setRequiredWithInRadius] = useState(null);
  const [volunteerShiftsLoading, setVolunteerShiftsLoading] = useState(false);
  const coordinatorOnly = userJson.jobTitle === "Volunteer Coordinator"

  const setStartDate = (date, dateString) => {
    if (date !== null) {
      let selectedDate = moment(date._d).format();
      selectedDate = convertDateToSODTimestamp(selectedDate);
      setVisitStartDate(selectedDate);
      setCarePlan(prevData => ({ ...prevData, visitStartDate: selectedDate }));
    }
    else setVisitStartDate("")
  }

  const submitCarePlan = async () => {
    setCarePlanLoading(true)
    const typesOfServicesArray = Array.isArray(typesOfServicesSelected)
      ? typesOfServicesSelected
      : [typesOfServicesSelected].filter(Boolean);
    const services = typesOfServicesArray.filter((item, index, self) =>
      index === self.findIndex(ele => ele.label === item.label)
    );

    const opportunitiesList = broadcastData?.selectedUsers?.map(item => ({
      workforceIdentifier: item?.userIdentifier,
      organizationIdentifier,
      frequencyPeriod: carePlan?.frequencyPeriod,
      frequency: JSON.stringify(carePlan?.frequency),
      services: JSON.stringify(services.map(item => item?.value)),
      opportunityStatus: isShared ? "Shared" : "Assigned",
      coordinatorIdentifier: userJson?.userIdentifier
    })) || [];

    // Only include Assigned/Shared in payload; Unassigned means volunteer was removed from slot.
    // const assignedOrSharedOpportunities = (opportunities || []).filter(
    //   (o) => o.opportunityStatus === "Assigned" || o.opportunityStatus === "Shared"
    // );
    const uniqueOpportunities = uniqueOpportunitiesByWorkforce(opportunities, {
      stripWorkforce: true,
    });

    // Slot-based (no carePlanId): merge slot-assigned volunteers with "share with more" (broadcastData)
    const sharedFromBroadcast = (broadcastData?.selectedUsers || [])
      .filter((u) => !uniqueOpportunities.some((o) => o.workforceIdentifier === u?.userIdentifier))
      .map((item) => ({
        workforceIdentifier: item?.userIdentifier,
        organizationIdentifier,
        frequencyPeriod: carePlan?.frequencyPeriod,
        frequency: JSON.stringify(carePlan?.frequency),
        services: JSON.stringify(services.map((s) => s?.value)),
        opportunityStatus: isShared ? "Shared" : "Assigned",
        coordinatorIdentifier: userJson?.userIdentifier,
      }));
    // Final list is always unique by workforceIdentifier (slot-assigned + shared merge included).
    const slotBasedOpportunities =
      carePlan?.frequencyPeriod === "slot based" && !carePlanId
        ? uniqueOpportunitiesByWorkforce([
            ...uniqueOpportunities,
            ...sharedFromBroadcast,
          ])
        : null;

    // Assign flow stays Active unless every opportunity in this list is Shared.
    const assignVolunteerStatus =
      opportunitiesList.length > 0 &&
      opportunitiesList.every((item) => item?.opportunityStatus === "Shared")
        ? "Shared"
        : "Active";

    const payload = {
      patientIdentifier: patientName?.value && JSON.parse(patientName?.value)?.userIdentifier,
      givenName: patientName?.givenName,
      familyName: patientName?.familyName,
      coordinatorIdentifier: userJson?.userIdentifier,
      organizationIdentifier,
      startDate: carePlan?.visitStartDate || null,
      frequencyPeriod: carePlan?.frequencyPeriod,
      frequency: JSON.stringify(carePlan?.frequency),
      services: JSON.stringify(services.map(item => item?.value)),
      patientAvailability: JSON.stringify(carePlan?.patientAvailability),
      patientAvailabilityComments: carePlan?.patientAvailabilityComments,
      additionalComments: carePlan?.carePlanComments,
      requestDate: carePlan?.requestDate,
      requestSource: typesOfSourceSelected?.label || "",
      teams: JSON.parse(patientName?.value || "{}")?.teams,
      status: assignVolunteer && opportunitiesList?.length ? assignVolunteerStatus : carePlanId ? carePlan?.status : isShared ? "Shared" : (carePlan?.frequencyPeriod === "slot based" ? (slotBasedOpportunities || uniqueOpportunities)?.length : opportunitiesList?.length) ? 'Active' : "Pending",
      opportunities: slotBasedOpportunities ?? (carePlan?.frequencyPeriod === "slot based" ? uniqueOpportunities : (carePlanId ? (!opportunities.length ? opportunitiesList : uniqueOpportunities) : opportunitiesList)),
      acceptanceLimit,
      requiredWithInRadius: broadcastData?.distanceValue || requiredWithInRadius,
      ...(carePlanId && { createdTimestamp: carePlanData?.createdTimestamp, updatedTimestamp: carePlanData?.updatedTimestamp }),
    }

    const workforceLookup = collectWorkforceById({
      opportunities,
      selectedUsers: broadcastData?.selectedUsers,
      previousOpportunities: carePlanData?.opportunities,
      frequency: carePlan?.frequency,
    });
    const eventCurrentValues = {
      ...payload,
      ...(carePlanId ? { id: carePlanId } : {}),
      opportunities: withWorkforceNames(payload.opportunities, workforceLookup),
    };

    if (carePlanId) {
      await apiService.post("niramayafoundation/niramayavisits/api/v1/careplan", { ...payload, id: carePlanId }, strings.UPDATE_OPPORTUNITY_SUCCESS, strings.UPDATE_OPPORTUNITY_ERROR);
      publishEvent("niramaya.volunteer.request.updated", userJson, eventCurrentValues, carePlanData)
    } else {
      await apiService.post('niramayafoundation/niramayavisits/api/v1/careplan', payload, strings.REQUEST_VOLUNTEERING_SERVICE_SUCCESS, strings.REQUEST_VOLUNTEERING_SERVICE_ERROR);
      publishEvent("niramaya.volunteer.request.created", userJson, eventCurrentValues)
    }
    setCarePlanLoading(false)
    navigate(-1);
  }

  // Volunteer confirms the shifts they picked up: the care plan is posted back exactly as the
  // server last returned it, with only the slot data replaced.
  const handleConfirmShifts = async () => {
    if (!carePlanId) return;
    setVolunteerShiftsLoading(true);

    const payload = {
      ...carePlanData,
      id: carePlanId,
      frequency: JSON.stringify(carePlan?.frequency),
    };

    const response = await apiService.post("niramayafoundation/niramayavisits/api/v1/careplan", payload);
    setVolunteerShiftsLoading(false);

    if (response?.id) {
      publishEvent("niramaya.volunteer.request.slot.updated", userJson, payload, carePlanData);
      notification.success({ message: strings.UPDATE_OPPORTUNITY_SUCCESS, placement: "bottomRight" });
      navigate(-1);
      return;
    }

    notification.error({
      message: carePlanErrorMessages[response?.code] || strings.UPDATE_OPPORTUNITY_ERROR,
      placement: "bottomRight",
    });
  };

  const handleAdditionalComments = (event) => {
    setCarePlan(prevData => ({ ...prevData, carePlanComments: event.target.value }))
  }

  const handlePatientAvailabilityComments = (event) => {
    setCarePlan(prevData => ({ ...prevData, patientAvailabilityComments: event.target.value }))
  }

  const submitButtonDisableText = (data) => {
    return (
      <div className='disable-btn-tooltip-data'>
        <div>Please select the following to continue!</div>
        <ul>
          {data?.map(item => <li key={item}>{item}</li>)}
        </ul>
      </div>
    )
  }

  const onRequestDateChange = (formattedDate) => {
    setCarePlan((prev) => ({
      ...prev,
      requestDate: convertDateToSODTimestamp(formattedDate),
    }));
  }

  const handleVolunteerSuggestionOK = () => {
    const typesOfServicesArray = Array.isArray(typesOfServicesSelected)
      ? typesOfServicesSelected
      : [typesOfServicesSelected].filter(Boolean);
    const services = 
      typesOfServicesArray
    .filter(
      (item, index, self) =>
        index === self.findIndex((ele) => ele.label === item.label)
    );

    const list =
      broadcastData?.selectedUsers?.map((item) => ({
        workforceIdentifier: item?.userIdentifier,
        organizationIdentifier,
        frequencyPeriod: carePlan?.frequencyPeriod,
        frequency: JSON.stringify(carePlan?.frequency),
        services: JSON.stringify(services.map((item) => item?.value)),
        opportunityStatus: shareWithSelectedVolunteers ? "Shared" : "Assigned",
        coordinatorIdentifier: userJson?.userIdentifier,
        workforce: item,
      })) || [];

    const opportunitiesMap = new Map(
      opportunities
        .filter((o) => o?.workforceIdentifier)
        .map((opportunity) => [opportunity.workforceIdentifier, opportunity])
    );
    list.forEach((item) => {
      if (!item?.workforceIdentifier) return;
      if (opportunitiesMap.has(item.workforceIdentifier)) {
        opportunitiesMap.get(item.workforceIdentifier).opportunityStatus =
          item.opportunityStatus;
      } else {
        opportunitiesMap.set(item.workforceIdentifier, { ...item });
      }
    });

    setOpportunities(Array.from(opportunitiesMap.values()));
    closeVolunteerSuggestionModal();
  };

  const openVolunteerSuggestion = () => {
    setShareWithSelectedVolunteers(false);
    setOpenVolunteerSuggestionModal(true);
  };

  const closeVolunteerSuggestionModal = () => {
    setOpenVolunteerSuggestionModal(false);
    setShareWithSelectedVolunteers(false);
  };

  const customModelButtons = (
    <>
      <SecondaryButton buttonText={"Cancel"} handleOnClick={closeVolunteerSuggestionModal} />
      <PrimaryButton buttonText={"OK"} handleOnClick={handleVolunteerSuggestionOK} />
    </>
  )

  useEffect(() => {
    const timeOfTheDay = Object.keys(duration).filter(key => duration[key]);
    setCarePlan(prev => ({ ...prev, patientAvailability: timeOfTheDay }))
  }, [duration])

  // button disability
  useEffect(() => {
    const requiredFields = [];

    if (!JSON.parse(patientName?.value || "{}")?.userIdentifier) requiredFields.push("Patient");
    if (!visitStartDate && isStartDateRequired) requiredFields.push("Visit Start Date");
    if (!typesOfServicesSelected?.length) requiredFields.push("Type of Service Required");
    if (carePlan?.frequencyPeriod === "weekly" && !carePlan?.frequency?.[0]?.range?.[1]) requiredFields.push("End date is required");

    setSubmitBtnDisable(requiredFields.length > 0);
    setSubmitBtnDisableText(submitButtonDisableText(requiredFields));
  }, [isStartDateRequired, visitStartDate, typesOfServicesSelected, carePlan?.frequency, carePlan?.frequencyPeriod, patientName?.value]);

  // get care plan details if careplan id is available
  useEffect(() => {
    const getCarePlanDetails = async () => {
      const endpoint = "niramayafoundation/niramayavisits/api/v1/careplans";
      const params = {
        organizationIdentifier,
        carePlanId,
      };
      const response = await apiService.get(endpoint, params);
      const result = response[0];

      // Slot-based: one opportunity per workforceIdentifier (API may return duplicates).
      setOpportunities(
        result?.frequencyPeriod === "slot based"
          ? uniqueOpportunitiesByWorkforce(result.opportunities)
          : result.opportunities
      );

      const services = JSON.parse(result?.services).map(item => {
        const service = JSON.parse(item)
        return {
          label: service.title,
          value: item,
          key: item
        }
      });
      const patientAvailability = JSON.parse(result.patientAvailability);
      const defaultKeys = ["Morning", "Afternoon", "Evening"];
      const availability = defaultKeys.reduce((acc, key) => {
        acc[key] = patientAvailability.includes(key);
        return acc;
      }, {});

      setCarePlanData(result)
      setVisitStartDate(result?.startDate)
      setCarePlan({
        visitStartDate: result?.startDate,
        requestDate: result?.requestDate,
        carePlanComments: result?.additionalComments,
        patientAvailabilityComments: result?.patientAvailabilityComments,
        patientAvailability: JSON.parse(result.patientAvailability),
        frequencyPeriod: result?.frequencyPeriod,
        frequency: JSON.parse(result?.frequency),
        status: result?.status
      })
      setDuration(availability)
      setRequiredWithInRadius(result?.requiredWithInRadius)
      setAcceptanceLimit(result?.acceptanceLimit)
      setTypesOfServicesSelected(services)
      setTypesOfSourceSelected({
        label: result?.requestSource,
        value: result?.requestSource,
      })
      setPatientName({
        label: getFullName(result?.patient?.givenName, result?.patient?.familyName),
        value: JSON.stringify(result?.patient),
        misc: result?.patient?.birthDate ? `MRN: ${result?.patient?.medicalRecordNumber} | DOB: ${moment(result?.patient?.birthDate)?.format('MMM D, YYYY')}` : `MRN: ${result?.patient?.medicalRecordNumber}`,
        preferences: result?.patient?.preferences,
        givenName: result?.patient?.givenName,
        familyName: result?.patient?.familyName,
      })
    }

    if (carePlanId) {
      getCarePlanDetails();
    }
  }, [carePlanId, organizationIdentifier]);

  // get patient details if careplan id is not available
  useEffect(() => {
    const getPatientDetails = async () => {
      const payload = {
        userIdentifier: patientIdentifier,
        secretKey: secretKey?.key,
      };
      const userData = await executeIntent(IntentType.GET_PATIENT_DETAILS_BY_USER_IDENTIFIER, payload);
      setPatientName({
        label: getFullName(userData[0]?.givenName, userData[0]?.familyName),
        value: JSON.stringify(userData[0]),
        misc: userData[0]?.birthDate ? `MRN: ${userData[0]?.medicalRecordNumber} | DOB: ${moment(userData[0]?.birthDate)?.format('MMM D, YYYY')}` : `MRN: ${userData[0]?.medicalRecordNumber}`,
        preferences: userData[0]?.preferences,
        givenName: userData[0]?.givenName,
        familyName: userData[0]?.familyName,
      })
    }

    if (secretKey && patientIdentifier) {
      getPatientDetails();
    }
  }, [patientIdentifier, configuration, secretKey]);

  useEffect(() => {
    const getConfig = async () => {
      const configPayload = {
        intent: IntentType.GET_CONFIGURATION,
        payload: {
          configurationKey: ["niramaya.careplan.startdate.required", "niramaya.featureaccess.menuheader.teamselect", "niramaya.volunteer.request.months"],
        },
        context: { organizationIdentifier },
      };

      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(configPayload)
        );
        const response = await result.json();

        const startDateRequired = JSON.parse(response[0].configurationValue || "{}")?.required
        setIsStartDateRequired(startDateRequired)

        const teamRequired = JSON.parse(response[1].configurationValue || "{}")?.visibility
        setIsTeamRequired(teamRequired)

        const rawMonths = response[2]?.configurationValue;
        let monthsCount = 7;
        if (rawMonths != null && rawMonths !== "") {
          try {
            const parsedMonths =
              typeof rawMonths === "number" ? rawMonths : JSON.parse(rawMonths);
            monthsCount =
              typeof parsedMonths === "number"
                ? parsedMonths
                : typeof parsedMonths === "string" &&
                    !Number.isNaN(Number(parsedMonths))
                  ? Number(parsedMonths)
                  : parsedMonths?.months ?? parsedMonths?.count ?? 7;
          } catch {
            const direct = Number(rawMonths);
            monthsCount =
              !Number.isNaN(direct) && direct > 0 ? direct : 7;
          }
        }
        setDefaultMonths(monthsCount);
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };

    getConfig();
  }, [organizationIdentifier]);

  useEffect(() => {
    setIsShared(broadcastData?.selectedUsers?.length > 1 ? true : false)
  }, [broadcastData?.selectedUsers?.length])

  // For slot-based frequency: keep opportunities in sync with slot assignments.
  // One opportunity per workforceIdentifier (same volunteer on multiple slots → one opportunity).
  // Ensures removing a volunteer clears the slot and assigning another replaces the previous (override).
  // Runs for both create (!carePlanId) and edit (carePlanId) so UI and payload reflect latest assignments.
  // Coordinator only: a volunteer's own pick-up is saved through handleConfirmShifts instead.
  useEffect(() => {
    if (!coordinatorOnly) return;
    if (carePlan?.frequencyPeriod !== "slot based" || !carePlan?.frequency?.[0]?.slots?.length) return;
    const slotData = carePlan.frequency[0];
    const slotsWithVolunteer = slotData.slots.filter((s) => s.assignedVolunteer);
    // Dedupe volunteers across slots: one workforce → one opportunity
    const uniqueByVolunteer = [
      ...new Map(
        slotsWithVolunteer
          .filter((slot) => slot.assignedVolunteer?.userIdentifier)
          .map((slot) => [
            slot.assignedVolunteer.userIdentifier,
            slot.assignedVolunteer,
          ])
      ).values(),
    ];
    const newIds = uniqueByVolunteer.map((v) => v?.userIdentifier).filter(Boolean).sort();

    const buildOpportunitiesFromSlots = (prev) => {
      const prevByWorkforce = new Map(
        (prev || [])
          .filter((o) => o?.workforceIdentifier)
          .map((o) => [o.workforceIdentifier, o])
      );
      // Compare assigned workforce ids only (ignore Unassigned leftovers / duplicates)
      const prevAssignedIds = [...prevByWorkforce.values()]
        .filter((o) => o.opportunityStatus === "Assigned")
        .map((o) => o.workforceIdentifier)
        .filter(Boolean)
        .sort();
      if (
        prevAssignedIds.length === newIds.length &&
        prevAssignedIds.every((id, i) => id === newIds[i])
      ) {
        // Still collapse any duplicate workforceIdentifier rows in state
        const deduped = uniqueOpportunitiesByWorkforce(prev);
        return deduped.length === (prev || []).length ? prev : deduped;
      }
      if (newIds.length === 0) {
        // No slots assigned: clear or set existing to Unassigned for edit mode
        if (carePlanId && prev?.length) {
          return uniqueOpportunitiesByWorkforce(
            prev.map((o) => ({
              ...o,
              opportunityStatus: "Unassigned",
            }))
          );
        }
        return [];
      }
      const typesOfServicesArray = Array.isArray(typesOfServicesSelected)
        ? typesOfServicesSelected
        : [typesOfServicesSelected].filter(Boolean);
      const services = typesOfServicesArray.filter(
        (item, index, self) =>
          index === self.findIndex((ele) => ele.label === item.label)
      );
      // Merge with existing opportunity (preserve id / server fields) so resubmit doesn't create duplicates
      const assignedList = uniqueByVolunteer.map((workforce) => {
        const workforceIdentifier = workforce?.userIdentifier;
        const existing = prevByWorkforce.get(workforceIdentifier);
        return {
          ...(existing || {}),
          workforceIdentifier,
          organizationIdentifier,
          frequencyPeriod: carePlan.frequencyPeriod,
          frequency: JSON.stringify(carePlan.frequency),
          services: JSON.stringify(
            services.map((item) => item?.value).filter(Boolean)
          ),
          opportunityStatus: "Assigned",
          coordinatorIdentifier: userJson?.userIdentifier,
          workforce,
        };
      });
      // Edit mode: keep any existing opportunities not in current slots as Unassigned (unique by workforce)
      if (carePlanId && prev?.length) {
        const assignedIds = new Set(newIds);
        const unassignedFromPrev = uniqueOpportunitiesByWorkforce(
          [...prevByWorkforce.values()]
            .filter((o) => !assignedIds.has(o.workforceIdentifier))
            .map((o) => ({ ...o, opportunityStatus: "Unassigned" }))
        );
        return uniqueOpportunitiesByWorkforce([
          ...assignedList,
          ...unassignedFromPrev,
        ]);
      }
      return assignedList;
    };

    setOpportunities(buildOpportunitiesFromSlots);
  }, [carePlan.frequencyPeriod, carePlan.frequency, typesOfServicesSelected, organizationIdentifier, userJson?.userIdentifier, carePlanId, coordinatorOnly]);

  return (
    <div>
      <div className='main-header-section'>
        <div className="form-renderer-back-button flex-grow-1">
          <BackButton />
          <p>Request Volunteering Service</p>
        </div>
        {
          carePlanId && <SelectCarePlanStatus status={carePlan?.status} setCarePlan={setCarePlan} coordinatorOnly={coordinatorOnly} />
        }
        <CalenderAndDate onDateChange={onRequestDateChange} date={carePlan?.requestDate} label={"Requested Date:"} readOnly={!coordinatorOnly} right={0} />
      </div>
      <div className='main-background-card'>
        <div className='main-content-area'>
          <SelectPatientName patientName={patientName} setPatientName={setPatientName} showCancel={Boolean(showCancel) ? true : false} />
          <SelectTypeOFServices typesOfServicesSelected={typesOfServicesSelected} setTypesOfServicesSelected={setTypesOfServicesSelected} readOnly={!coordinatorOnly} className="section-two" />

          <div className='section-three'>
            <span>Schedule Details</span>
            <div className='section-three-subsection'>
              <span className={isStartDateRequired && 'required-mark-label'}>Start Date</span>
              <div className="mt-2">
                <DatePicker disabled={!coordinatorOnly} value={carePlan?.visitStartDate ? moment(carePlan?.visitStartDate) : undefined} disabledDate={disabledDate} placeholder="MM/DD/YYYY" format="MM/DD/YYYY" onChange={setStartDate} allowClear={false} />
              </div>
            </div>

            <div className='section-three-subsection'>
              <span>Volunteer Frequency Period</span>
              <VolunteerFrequency setCarePlan={setCarePlan} visitStartDate={visitStartDate} carePlan={carePlan} readOnly={!coordinatorOnly} patientName={patientName} typesOfServicesSelected={typesOfServicesSelected} defaultMonths={defaultMonths} onConfirmShifts={carePlanId ? handleConfirmShifts : null} confirmShiftsLoading={volunteerShiftsLoading} />
            </div>

            <div className='section-three-subsection'>
              <span>Patient’s Availability</span>
              <MultiSelectCards renderOptions={timeOfTheDay} setOptions={setDuration} defaultSetOptions={duration} readOnly={!coordinatorOnly} />
              <TextArea readOnly={!coordinatorOnly} value={carePlan?.patientAvailabilityComments} className='section-three-subsection-textarea' rows={2} placeholder="Patient's Availability Comments" onChange={handlePatientAvailabilityComments} />
            </div>

            <div className='section-three-subsection'>
              <span>Additional Comments</span>
              <TextArea readOnly={!coordinatorOnly} value={carePlan?.carePlanComments} className='section-three-subsection-textarea' rows={4} onChange={handleAdditionalComments} />
            </div>
            {carePlanId && !assignVolunteer && coordinatorOnly && (
              <div className="section-three-subsection d-flex flex-column gap-1">
                {Boolean(requiredWithInRadius) && (
                  <span>
                    Volunteer Distance (Miles): {requiredWithInRadius}
                  </span>
                )}
                <div>
                  <span style={{fontSize: 14}}>How many volunteers can accept this request?</span>
                  <div>
                    <Select
                      value={acceptanceLimit}
                      defaultValue={0}
                      onChange={(value) => setAcceptanceLimit(value)}
                      style={{ width: 280 }}
                      placeholder="Select volunteer acceptance limit"
                    >
                      <Select.Option value={0}>
                        All selected volunteers can accept
                      </Select.Option>
                      {[...Array(15)].map((_, i) => (
                        <Select.Option key={i + 1} value={i + 1}>
                          {i + 1} volunteer{i + 1 > 1 ? "s" : ""}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            )}
            <div className='section-three-subsection'>
              {
                opportunities.length && !assignVolunteer ? (
                  <>
                    {
                      coordinatorOnly &&
                      <>
                        <VolunteerTable opportunities={opportunities} setOpportunities={setOpportunities} carePlan={carePlan} setCarePlan={setCarePlan} />
                        {
                          !(carePlan?.frequencyPeriod === 'slot based' && !carePlanId) &&
                          <div>
                            <span
                              className="assign-btn mb-4"
                              onClick={openVolunteerSuggestion}
                            >
                              <SmartIcon iconKey={"volunteerRequest"} color="var(--main-theme-color)" />
                              <span>Assign Volunteers</span>
                            </span>
                          </div>
                        }
                      </>
                    }
                    <VolunteerActivityTable carePlanData={carePlanData} volunteerId={!coordinatorOnly && userJson.userIdentifier} />
                    {/* Slot-based (no carePlanId): also show volunteer selection so coordinator can share with more volunteers who will pick a slot */}
                    {!carePlanId && carePlan?.frequencyPeriod === "slot based" && (
                      <>
                        <span>Share with more volunteers</span>
                        <div className='section-three-subsection-container'>
                          <WorkforceSuggestionWrapper setBroadcastData={setBroadcastData} typesOfServicesSelected={typesOfServicesSelected} patientName={patientName} opportunities={opportunities} showSelectAll={true} isMultiSelect={true} filterRequired={true} selectedFilters={broadcastData?.selectedFilters} isTeamRequired={isTeamRequired} />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <span>Volunteer Selection</span>
                    <div className='section-three-subsection-container'>
                      <WorkforceSuggestionWrapper setBroadcastData={setBroadcastData} typesOfServicesSelected={typesOfServicesSelected} patientName={patientName} opportunities={opportunities} showSelectAll={true} showDetails={true} isMultiSelect={true} filterRequired={true} selectedFilters={broadcastData?.selectedFilters} isTeamRequired={isTeamRequired} />
                    </div>
                  </>
                )
              }
            </div>
            {(!carePlanId || assignVolunteer) && (
              <div className="section-three-subsection">
                <Checkbox
                  onChange={(e) => setIsShared(e.target.checked)}
                  checked={isShared}
                >
                  Send this request to volunteers so they can accept or reject it.
                </Checkbox>
              </div>
            )}
            {isShared && (
              <div className="section-three-subsection">
                <span>How many volunteers can accept this request?</span>
                <div>
                  <Select
                    value={acceptanceLimit}
                    defaultValue={0}
                    onChange={(value) => setAcceptanceLimit(value)}
                    style={{ width: 280 }}
                    placeholder="Select volunteer acceptance limit"
                  >
                    <Select.Option value={0}>
                      All selected volunteers can accept
                    </Select.Option>
                    {[...Array(15)].map((_, i) => (
                      <Select.Option key={i + 1} value={i + 1}>
                        {i + 1} volunteer{i + 1 > 1 ? "s" : ""}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>
        {coordinatorOnly && (
          <div className="submit-btn">
            <PrimaryButton
              buttonText="Submit"
              loading={carePlanLoading}
              handleOnClick={submitCarePlan}
              buttonDisable={!coordinatorOnly || submitBtnDisable}
              tooltipTitle={submitBtnDisable && submitBtnDisableText}
            />
          </div>
        )}
      </div>
      <div>
        {openVolunteerSuggestionModal && (
          <CustomModal
            closable={true}
            isModalOpen={openVolunteerSuggestionModal}
            footer={[customModelButtons]}
            closeModal={closeVolunteerSuggestionModal}
            title="Volunteer Selection">
            <WorkforceSuggestionWrapper setBroadcastData={setBroadcastData} typesOfServicesSelected={typesOfServicesSelected} patientName={patientName} opportunities={opportunities} showSelectAll={true} isMultiSelect={true} filterRequired={true} selectedFilters={broadcastData?.selectedFilters} isTeamRequired={isTeamRequired} />
            <div className="section-three-subsection">
              <Checkbox
                onChange={(e) => setShareWithSelectedVolunteers(e.target.checked)}
                checked={shareWithSelectedVolunteers}
              >
                Send this request to volunteers so they can accept or reject it.
              </Checkbox>
            </div>
          </CustomModal>
        )}
      </div>
    </div>
  )
}

export default RequestForVolunteeringService